import {Component, OnInit, effect, input, model, output, signal, computed, ChangeDetectionStrategy} from "@angular/core"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-numeric-input",
    templateUrl: "./numeric-input.component.html",
    styleUrls: ["./numeric-input.component.scss"],
})
export class NumericInputComponent implements OnInit {
    readonly $disabled = input(false, {alias: "disabled"})
    readonly $value = input<number | undefined>(undefined, {alias: "value"})
    readonly $decimalPlaces = input<number | undefined>(undefined, {alias: "decimalPlaces"})
    readonly $type = input<"int" | "float">("float", {alias: "type"})
    readonly $emptyValue = model<null | undefined | number | "disallow">("disallow", {alias: "emptyValue"})
    readonly $convert = input<{
        toDisplay: (x: number) => number
        fromDisplay: (x: number) => number
    }>(undefined, {alias: "convert"})
    readonly valueChange = output<number | null | undefined>()
    readonly $placeholder = input<string>(undefined, {alias: "placeholder"})
    readonly $readonly = input(false, {alias: "readonly"})
    readonly $isButton = input(false, {alias: "isButton"})

    private readonly $editing = signal(false)
    private readonly $editString = signal<string | null>(null)

    readonly $optional = input<boolean>(undefined, {alias: "optional"})

    constructor() {
        effect(() => {
            const x = this.$optional()
            if (x !== undefined) {
                this.$emptyValue.set(x ? undefined : "disallow")
            }
        })
    }

    setDisplayString(valueStr: string) {
        if (this.$editing()) {
            this.$editString.set(valueStr)
        }
        if (valueStr == null || valueStr.trim() === "") {
            const emptyValue = this.$emptyValue()
            if (emptyValue === null) {
                this.valueChange.emit(null)
            } else if (emptyValue === undefined) {
                this.valueChange.emit(undefined)
            } else if (typeof emptyValue === "number") {
                this.valueChange.emit(emptyValue)
            } else {
                return // not allowed
            }
        }
        let value = this.$type() === "int" ? parseInt(valueStr) : parseFloat(valueStr)
        if (value == null || isNaN(value)) return
        const convert = this.$convert()
        if (convert) value = convert.fromDisplay(value)
        if (value == null || isNaN(value)) return
        this.valueChange.emit(value)
    }

    readonly $displayString = computed(() => {
        if (this.$editing()) {
            return this.$editString()
        }
        let value = this.$value() ?? 0
        const convert = this.$convert()
        const places = this.$type() === "int" ? 0 : this.$decimalPlaces()
        if (convert) value = convert.toDisplay(value)
        if (this.$value() == null) return null
        if (places != null) {
            return Number(value).toFixed(places)
        } else {
            return value.toString()
        }
    })

    onFocus() {
        this.$editing.set(false)
        this.$editString.set(this.$displayString())
        this.$editing.set(true)
    }

    onBlur() {
        this.$editing.set(false)
        this.$editString.set(null)
    }

    ngOnInit(): void {}
}
